// ============================================================================
// Mesa de Servicio — canal WhatsApp (grupos de clientes): capa de servicio.
// ============================================================================
// Habla con /serviceDesk/whatsapp/* del torre-de-control-api. Todas las rutas
// son de AGENTE de la mesa: quien no lo sea recibe 403 y la sección lo enseña
// con el panel compartido `DeskLoadError` (no cierra la sesión).
//
// Nada aquí decide permisos ni política: el veredicto de cada mensaje
// (`blocked_unlinked`, `staff_message`, `ask_identity`, `needs_confirmation`,
// `eligible`) lo calcula el backend. La pantalla sólo lo muestra.
// ============================================================================
import api from "../../../utils/api";
import { unwrap } from "./envelope";
import type { TimestampLike } from "./serviceDeskService";

// --- Tipos ------------------------------------------------------------------

/** Qué conector está detrás del canal. `cloud` lo implementa el issue #548. */
export type WaConnectorId = "simulator" | "cloud";

export interface WaConnectorStatus {
  id: WaConnectorId | string;
  implemented: boolean;
  configured?: boolean;
  canSend?: boolean;
  canCreateGroups?: boolean;
  maxParticipants?: number;
  simulated?: boolean;
  groups?: number;
  pendingEvents?: number;
  pendingIssue?: number;
  /**
   * "company" en el conector oficial (#712): el número, el token y la WABA son
   * los de la compañía del grupo (su cuenta de canal), así que crear un grupo exige
   * elegir la compañía.
   */
  credentialsScope?: "company";
  detail?: string;
}

export interface WaChannelSettings {
  enabled: boolean;
  /** "auto" = manda el despliegue (`SD_WA_CONNECTOR`). */
  connector: WaConnectorId | "auto";
  requireContactConfirmation: boolean;
  askUnknownParticipants: boolean;
  /** Plantilla fuera de la ventana de 24 h (#715); ver WhatsappGroupsSettings. */
  replyTemplate?: { name: string; language: string; includeReference: boolean } | null;
}

// Mismas reglas que el backend (waConstants.sanitizeReplyTemplate, #715): lo
// que no las cumple el servidor lo guarda como «sin plantilla».
const TEMPLATE_NAME_RE = /^[a-z0-9_]{1,512}$/;
const TEMPLATE_LANGUAGE_RE = /^[a-z]{2,3}(?:_[A-Z]{2})?$/;

/** ¿La plantilla fuera de la ventana de 24 h es válida? `null` (ninguna) también lo es. */
export function isValidReplyTemplate(tpl: { name: string; language: string } | null | undefined): boolean {
  if (!tpl) return true;
  return TEMPLATE_NAME_RE.test(tpl.name) && TEMPLATE_LANGUAGE_RE.test(tpl.language);
}

/**
 * Qué conector aplica y POR QUÉ (#716 · D8). Precedencia del backend
 * (waConnector.describeConnectorResolution): elección explícita en
 * Configuración → variable `SD_WA_CONNECTOR` del despliegue → simulador.
 */
export type WaConnectorSource = "request" | "settings" | "env" | "default";

export interface WaConnectorResolution {
  id: WaConnectorId | string;
  source: WaConnectorSource;
  /** Lo guardado en Configuración ("auto" si no hay elección explícita). */
  settingsValue: WaConnectorId | "auto";
  /** Valor crudo de `SD_WA_CONNECTOR` en el despliegue (o null). */
  envValue: string | null;
  envValid: boolean;
  /** La variable pide OTRO conector y la elección guardada la está tapando. */
  envIgnored: boolean;
  /** Explicación lista para mostrar. */
  reason: string;
}

export const CONNECTOR_SOURCE_LABEL: Record<WaConnectorSource, string> = {
  request: "pedido por la operación",
  settings: "elegido en Configuración",
  env: "variable SD_WA_CONNECTOR",
  default: "valor por omisión",
};

export function connectorLabel(id: string | null | undefined): string {
  if (id === "simulator") return "Simulador (sin número real)";
  if (id === "cloud") return "API oficial de grupos";
  if (id === "auto") return "Automático (según el despliegue)";
  return id || "—";
}

export interface WaChannelStatus {
  settings: WaChannelSettings;
  connector: WaConnectorStatus;
  /** Qué conector aplica y por qué (#716 · D8). Ausente en backends viejos. */
  connectorResolution?: WaConnectorResolution;
  /** Marcas que ESTE operador puede asignar a un grupo (ya filtradas). */
  brands?: { id: string; label: string }[];
  /**
   * ¿Este operador está acotado por marca? Si lo está, un grupo sin marca no lo
   * volvería a ver, así que la marca es OBLIGATORIA al crear. No se puede
   * deducir de `brands`: un admin sin acotar también recibe la lista entera.
   */
  brandRequired?: boolean;
  /**
   * ¿La ruta de simulación está habilitada en ESTE despliegue
   * (`SD_WA_SIMULATOR`)? El simulador escribe grupos, participantes y auditoría
   * de verdad, así que nace apagado: sin esto la pantalla ofrecería un botón
   * que responde 403.
   */
  simulatorEnabled?: boolean;
  /**
   * ¿Puede ESTE operador encender/apagar el bot de un grupo? (#556) Admin
   * reseller, o un rol de `waBotOperatorRoleIds`. Ausente en backends viejos.
   */
  canOperateBot?: boolean;
  maxParticipants: number;
  /** La política de #547, tal cual la enseña la pantalla. */
  policy: string;
}

/**
 * Conflicto de vínculo al confirmar un grupo creado (#713): el grupo pendiente
 * y el real (o el número que recibió el evento) dicen compañías distintas. El
 * servidor NO sobrescribe nada: lo resuelve un agente eligiendo la compañía.
 */
export interface WaLinkConflict {
  active: boolean;
  reason: "pending_other_company" | "real_other_company" | "number_other_company" | string;
  realGroupId?: string | null;
  pendingGroupId?: string | null;
  realCompanyId?: string | null;
  pendingCompanyId?: string | null;
  attributedCompanyId?: string | null;
  /** `number_other_company`: número por el que vive el grupo y eventos descartados. */
  phoneNumberId?: string | null;
  droppedCount?: number;
  at?: TimestampLike;
}

export interface WaUnlinkedWarning {
  active: boolean;
  count: number;
  firstAt?: TimestampLike;
  lastAt?: TimestampLike;
  /** Teléfono ENMASCARADO del último autor; nunca el número completo. */
  lastAuthorMasked?: string | null;
  message?: string;
}

export interface WaCompanySuggestion {
  companyId: string;
  companyName: string;
  confidence: "high" | "medium";
}

export interface WaGroup {
  id: string;
  connector: string | null;
  providerGroupId: string | null;
  subject: string | null;
  inviteLink: string | null;
  companyId: string | null;
  companyName: string | null;
  brand: string | null;
  linkedByName: string | null;
  linkedAt?: TimestampLike;
  participantCount: number;
  maxParticipants: number;
  createdBy: "business" | "unknown" | string;
  lastMessageAt?: TimestampLike;
  unlinkedWarning: WaUnlinkedWarning | null;
  linkConflict?: WaLinkConflict | null;
  status: string;
  createdAt?: TimestampLike;
  companySuggestion?: WaCompanySuggestion | null;
  /** Estado del bot en el grupo (#556). Ausente en backends viejos. */
  bot?: WaBotState;
  /**
   * Sólo en la respuesta de crear: con la API oficial el enlace de invitación
   * llega después por webhook, no en la respuesta (#712).
   */
  inviteLinkPending?: boolean;
}

/** Estado del bot de un grupo (#556). APAGADO por omisión. */
export interface WaBotState {
  enabled: boolean;
  /** Cuándo CONFIRMÓ WhatsApp el aviso de privacidad (sin aviso, el bot no habla). */
  noticeSentAt: string | null;
  /**
   * El aviso está en la bandeja de salida (#714) y aún no se confirma: el bot
   * calla mientras tanto. `noticeEntryId` es el envío (para pedir su reenvío).
   */
  noticePending?: boolean;
  noticeEntryId?: string | null;
  /**
   * El bot estaba encendido para OTRA compañía o marca del grupo: aquí cuenta
   * como apagado hasta que un operador lo vuelva a encender (y se publique el
   * aviso de privacidad para la nueva).
   */
  companyChanged?: boolean;
  enabledByName: string | null;
  /** Una persona del staff tomó el grupo: el bot se calla hasta `humanUntil`. */
  humanActive: boolean;
  humanUntil: string | null;
  humanByName: string | null;
  /**
   * Un cliente pidió en el grupo una persona («human») o que se desactive el
   * asistente («switch_off»). `switch_off` deja al bot callado SIN vencimiento
   * (`humanHold`) hasta que el staff devuelva el grupo al bot. `humanAlertedVia`:
   * por dónde se avisó al staff («email», «ticket_note»); vacío = sólo aquí.
   */
  humanRequested?: boolean;
  humanRequestedAt?: string | null;
  humanRequestKind?: "human" | "switch_off" | null;
  humanHold?: boolean;
  humanAlertedVia?: string[];
  limits: {
    maxRepliesPerHour: number;
    maxRepliesPerDay: number;
    maxDailyCostMicros: number;
    replyCostMicros: number;
  };
  usage: {
    hourCount: number;
    dayCount: number;
    dayCostMicros: number;
    /** Tope alcanzado hoy ("hour" | "day" | "cost"), si lo hay. */
    cappedScope: string | null;
    /** Respuestas encoladas con su hueco apartado, aún sin confirmar. */
    reserved?: number;
  };
}

export type WaLinkType = "unknown" | "user" | "crm_contact";
/**
 * `auto` = lo aplicó el conector porque la mesa no exige confirmación humana.
 * Hay persona resuelta (puede generar ticket) pero no lo ha mirado nadie.
 */
export type WaLinkState = "unknown" | "suggested" | "auto" | "confirmed";

export interface WaSuggestion {
  type: "user" | "crm_contact";
  id: string;
  name: string;
  companyId: string | null;
  roleName?: string | null;
  isStaff: boolean;
  confidence: "high" | "medium" | "low";
  companyMismatch: boolean;
  /**
   * Contacto del CRM que declara una empresa sin vínculo EXPLÍCITO con una
   * compañía de la plataforma (#716 · D3). No se vincula solo: un agente puede
   * confirmarlo, y la confirmación queda atada a la compañía del grupo.
   */
  companyUnresolved?: boolean;
  /**
   * El teléfono guardado o tecleado es ambiguo ("+" con 10 dígitos o menos, u
   * 11 dígitos sin "+"): puede ser esta persona, pero no se decide solo
   * (#716 · D9).
   */
  phoneAmbiguous?: boolean;
  phoneMatch?: "exact" | "ambiguous";
}

export interface WaParticipant {
  id: string;
  phone: string | null;
  phoneMasked: string | null;
  displayName: string | null;
  declaredName: string | null;
  declaredRole: string | null;
  linkType: WaLinkType;
  linkId: string | null;
  linkName: string | null;
  linkState: WaLinkState;
  isStaff: boolean;
  left: boolean;
  messageCount: number;
  suggestions: WaSuggestion[];
  confirmedByName: string | null;
  confirmedAt?: TimestampLike;
  lastSeenAt?: TimestampLike;
}

export interface WaAuditEntry {
  id: string;
  action: string;
  actorName: string | null;
  field: string | null;
  from: unknown;
  to: unknown;
  detail: string | null;
  createdAt?: TimestampLike;
}

/** Qué detectó el bot en un mensaje de grupo (#550). */
export type WaCaseCategory = "bug" | "how_to" | "change_request" | "follow_up" | "chit_chat";

/**
 * Un «caso»: la ráfaga de mensajes sobre un mismo tema. La clasificación y su
 * confianza las calcula el backend; el staff puede corregirlas.
 */
export interface WaCase {
  id: string;
  category: WaCaseCategory | null;
  confidence: number | null;
  /** `rules` (reglas), `example` (parecido a una corrección) o `staff`. */
  classificationSource: string | null;
  status: string | null;
  authorName: string | null;
  firstText: string | null;
  messageCount: number;
  firstAt?: TimestampLike;
  lastAt?: TimestampLike;
  ticketId: string | null;
  ticketReference: string | null;
  /** `company` = cuenta confirmada de la compañía; `public` = anónimo. */
  accessLevel: "company" | "public" | null;
  correctedByName: string | null;
  correctedAt?: TimestampLike;
}

export interface WaGroupDetail {
  group: WaGroup;
  participants: WaParticipant[];
  /** Casos detectados en el grupo (#550), los más recientes primero. */
  cases?: WaCase[];
  audit: WaAuditEntry[];
}

export type WaDecision =
  | "channel_disabled"
  | "blocked_unlinked"
  | "staff_message"
  | "ask_identity"
  | "needs_confirmation"
  | "eligible"
  | "participant_joined"
  | "participant_left"
  | "group_updated"
  | "duplicate";

export interface WaSimulationResult {
  processed: number;
  results: {
    groupId: string;
    decision: WaDecision;
    reason: string | null;
    /** Qué hizo la detección con el mensaje (#550): ver `ACTION_META`. */
    action?: string | null;
    category?: WaCaseCategory | null;
    ticketReference?: string | null;
  }[];
  outbox: { providerMessageId: string; groupId: string; text: string; status: string }[];
  /** Eventos que quedan en la cola del simulador (se consume al procesarla). */
  pendingEvents?: number;
}

// --- Llamadas ---------------------------------------------------------------

export async function getWhatsappStatus(): Promise<WaChannelStatus> {
  const res = await api.get("/serviceDesk/whatsapp/status");
  return unwrap(res);
}

export async function listWhatsappGroups(): Promise<{ groups: WaGroup[]; unlinked: number }> {
  const res = await api.get("/serviceDesk/whatsapp/groups");
  const data = unwrap(res);
  return { groups: data?.groups || [], unlinked: data?.unlinked || 0 };
}

export async function getWhatsappGroup(id: string): Promise<WaGroupDetail> {
  const res = await api.get(`/serviceDesk/whatsapp/groups/${encodeURIComponent(id)}`);
  return unwrap(res);
}

/**
 * Vincular (o desvincular, mandando `""`) el grupo a una compañía, y de paso
 * fijar su MARCA. La marca decide qué operadores pueden ver el grupo (alcance
 * por marca): mientras no la tenga, sólo lo ven quienes no están acotados.
 */
export async function linkGroupCompany(
  id: string,
  companyId: string,
  brand?: string | null,
): Promise<WaGroup> {
  const body: { companyId: string; brand?: string | null } = { companyId };
  if (brand !== undefined) body.brand = brand;
  const res = await api.put(`/serviceDesk/whatsapp/groups/${encodeURIComponent(id)}/company`, body);
  return unwrap(res);
}

export async function linkParticipant(
  groupId: string,
  participantId: string,
  patch: {
    linkType?: WaLinkType;
    linkId?: string | null;
    isStaff?: boolean;
    declaredName?: string;
    declaredRole?: string;
  },
): Promise<WaParticipant> {
  const res = await api.put(
    `/serviceDesk/whatsapp/groups/${encodeURIComponent(groupId)}/participants/${encodeURIComponent(participantId)}`,
    patch,
  );
  return unwrap(res);
}

/**
 * Crear el grupo por API. `brand` es OBLIGATORIA para un operador acotado por
 * marca (`status.brandRequired`): sin ella el backend responde 422, porque un
 * grupo sin marca ya no entraría en su propio alcance de lectura.
 */
export async function createWhatsappGroup(input: {
  subject: string;
  companyId?: string;
  brand?: string;
}): Promise<WaGroup> {
  const res = await api.post("/serviceDesk/whatsapp/groups", input);
  return unwrap(res);
}

/**
 * Sólo con el conector "simulator"; con el oficial el backend responde 409, con
 * el canal apagado también, y con `SD_WA_SIMULATOR` sin encender, 403 (es una
 * herramienta de desarrollo: escribe grupos y participantes reales).
 *
 * `brand` es la marca con la que NACEN los grupos que la simulación descubra.
 * Para un operador acotado a varias marcas es obligatoria: sin ella el backend
 * responde 422, porque un grupo sin marca ya no entraría en su propio alcance.
 *
 * La cola de eventos del simulador SE CONSUME: `reset: true` la repone desde el
 * archivo de ejemplo para poder repetir el recorrido entero (descubrir →
 * vincular → volver a simular) tantas veces como haga falta.
 */
export async function simulateWhatsappEvents(
  opts?: { events?: unknown[]; reset?: boolean; brand?: string } | unknown[],
): Promise<WaSimulationResult> {
  const body = Array.isArray(opts) ? { events: opts } : { ...(opts || {}) };
  const res = await api.post("/serviceDesk/whatsapp/simulate", body);
  return unwrap(res);
}

/**
 * Corrige la clasificación de un caso. Queda auditada en el grupo y el
 * clasificador la usa como ejemplo para los mensajes parecidos.
 */
export async function correctWhatsappCase(
  groupId: string,
  caseId: string,
  category: WaCaseCategory,
): Promise<WaCase> {
  const res = await api.put(
    `/serviceDesk/whatsapp/groups/${encodeURIComponent(groupId)}/cases/${encodeURIComponent(caseId)}/classification`,
    { category },
  );
  return unwrap(res);
}

/**
 * Encender/apagar el bot del grupo y/o ajustar sus topes (#556). Encenderlo la
 * PRIMERA vez publica el aviso de privacidad en el grupo; si no se puede
 * publicar, el backend no lo enciende. Los topes del grupo sólo pueden ser más
 * estrictos que los del despliegue.
 */
export async function updateGroupBot(
  groupId: string,
  patch: {
    enabled?: boolean;
    /** El aviso anterior PUDO llegar y el operador revisó el grupo: SÍ llegó (no se manda otro). */
    noticeConfirmed?: boolean;
    /** El aviso anterior PUDO llegar y el operador revisó el grupo: NO llegó (se publica otra vez). */
    republishNotice?: boolean;
    limits?: Partial<Pick<WaBotState["limits"], "maxRepliesPerHour" | "maxRepliesPerDay" | "maxDailyCostMicros">>;
  },
): Promise<WaGroup & { notice?: { entryId: string | null; status: string | null; confirmed: boolean } | null }> {
  const res = await api.put(`/serviceDesk/whatsapp/groups/${encodeURIComponent(groupId)}/bot`, patch);
  return unwrap(res);
}

/**
 * Traspaso a persona (#556): «take» publica «te atiende una persona» y calla al
 * bot en el grupo; «release» le devuelve la voz (sólo operadores del bot).
 */
export async function groupBotHandoff(
  groupId: string,
  action: "take" | "release",
): Promise<
  WaGroup & { handoffNoticeSent?: boolean; handoffNotice?: { entryId: string | null; status: string | null; error?: string } | null }
> {
  const res = await api.post(`/serviceDesk/whatsapp/groups/${encodeURIComponent(groupId)}/bot/handoff`, { action });
  return unwrap(res);
}

/** Texto corto del estado del bot para la ficha del grupo. */
export function botStatusLabel(bot: WaBotState | undefined | null): string {
  if (bot && !bot.enabled && bot.companyChanged) return "Apagado: el grupo cambió de compañía o marca (vuelve a encenderlo)";
  if (!bot || !bot.enabled) return "Apagado";
  if (!bot.noticeSentAt && bot.noticePending) return "Encendido: aviso de privacidad por confirmar (aún no habla)";
  if (!bot.noticeSentAt) return "Encendido, sin aviso de privacidad (no habla)";
  if (bot.humanActive && bot.humanHold) return "Callado: pidieron desactivar el asistente (hasta que lo devuelvas)";
  if (bot.humanActive && bot.humanByName) return `En pausa: te atiende ${bot.humanByName}`;
  if (bot.humanActive && bot.humanRequested) return "En pausa: un cliente pidió una persona";
  if (bot.humanActive) return "En pausa: te atiende una persona";
  if (bot.usage && bot.usage.cappedScope) return "En silencio: llegó a su tope";
  return "Encendido";
}

/**
 * Aviso destacado para el staff cuando un cliente pidió una persona o apagar
 * el asistente (se muestra aunque el bot esté apagado). null = nada que avisar.
 */
export function botHumanRequestNotice(bot: WaBotState | undefined | null): string | null {
  if (!bot || !bot.humanRequested) return null;
  const what =
    bot.humanRequestKind === "switch_off"
      ? "Un cliente pidió que se desactive el asistente: queda callado en este grupo hasta que lo devuelvas al bot o lo apagues."
      : "Un cliente pidió hablar con una persona: alguien del equipo tiene que contestar en el grupo.";
  const via = bot.humanAlertedVia || [];
  const how = via.length
    ? ` Se avisó por ${via.map((v) => (v === "email" ? "correo" : v === "ticket_note" ? "nota en el ticket" : v)).join(" y ")}.`
    : " No hay lista de avisos por correo configurada: sólo se ve aquí.";
  return what + how;
}

// --- Revisión: lo atorado en las bandejas (#728) ---------------------------

/** Lo mínimo del grupo de una fila de revisión (el backend ya aplicó el alcance). */
export interface WaReviewGroup {
  id: string;
  subject: string | null;
  companyId: string | null;
  companyName: string | null;
  brand: string | null;
  status: string;
}

/** Qué hizo el staff con una revisión (historial del propio documento). */
export interface WaReviewHistoryItem {
  action: string;
  by: string | null;
  byName: string | null;
  at: TimestampLike;
  note: string | null;
  reason?: string | null;
}

/** Evento de la bandeja de ENTRADA que espera a una persona. */
export interface WaInboxReviewItem {
  source: "inbox";
  id: string;
  /** `message`, `participant`, `group`… o `raw` (payload ilegible). */
  kind: string | null;
  action: string | null;
  reason: string | null;
  attempts: number;
  receivedAt?: TimestampLike;
  reviewAt?: TimestampLike;
  groupDocId: string | null;
  companyId: string | null;
  phoneNumberId: string | null;
  ticketIds: string[];
  /** Teléfono ENMASCARADO de quien escribió; nunca el número completo. */
  authorMasked: string | null;
  text: string | null;
  replies: { index: number; state: string | null; text: string | null; ticketId: string | null }[];
  /** Respuestas del bot que PUDIERON salir: reprocesar exige decidir qué hacer. */
  ambiguousReplies: number;
  reprocessCount: number;
  history: WaReviewHistoryItem[];
  actions: { reprocess: boolean; resendAmbiguous: boolean; resolve: boolean };
  group: WaReviewGroup | null;
}

/**
 * Envío (o archivo del cliente) de la bandeja de SALIDA que espera a una persona.
 * `delivery_status` (#730): un estado de entrega de WhatsApp que no se pudo
 * aplicar a su envío tras varios intentos (se reprocesa o se resuelve).
 */
export interface WaOutboxReviewItem {
  source: "outbox";
  id: string;
  type: "message" | "inbound_media" | "delivery_status" | string;
  status: string;
  unconfirmed: boolean;
  errorKind: string | null;
  dropReason: string | null;
  reason: string | null;
  attempts: number;
  failedAt?: TimestampLike;
  unconfirmedAt?: TimestampLike;
  groupDocId: string | null;
  companyId: string | null;
  phoneNumberId: string | null;
  ticketIds: string[];
  text: string | null;
  mediaType: string | null;
  resendCount: number;
  /** Sólo `delivery_status`: los estados de WhatsApp pendientes de aplicar y su envío. */
  deliveryStatuses?: string[];
  entryId?: string | null;
  actions: { resend: boolean; reprocess: boolean; resolve: boolean };
  group: WaReviewGroup | null;
}

export type WaReviewItem = WaInboxReviewItem | WaOutboxReviewItem;

/** Tráfico REAL de Meta que se está descartando (simulador o canal apagado). */
export interface WaDropAlert {
  id: string;
  reason: "connector_not_cloud" | "channel_disabled" | string;
  message: string;
  phoneNumberId: string | null;
  companyId: string | null;
  count: number;
  firstAt?: TimestampLike;
  lastAt?: TimestampLike;
  lastKind: string | null;
  groupDocIds: string[];
  lastEmailAt?: TimestampLike;
}

export interface WaReviewData {
  items: WaReviewItem[];
  alerts: WaDropAlert[];
  counts: { inbox: number; outbox: number; alerts: number };
  /** Hay más filas: se piden con `nextCursor` (lista ordenada y paginada, #730). */
  truncated: boolean;
  nextCursor: string | null;
  /** Sólo un operador sin acotar por marca ve (y atiende) las alertas por número. */
  canSeeAlerts: boolean;
}

/**
 * Una página de la revisión (lo más nuevo primero, #730). Sin `cursor`, la
 * primera (con las alertas); con el `nextCursor` de la anterior, la siguiente.
 */
export async function listWhatsappReview(opts: { cursor?: string | null; limit?: number } = {}): Promise<WaReviewData> {
  const params: Record<string, string | number> = {};
  if (opts.cursor) params.cursor = opts.cursor;
  if (opts.limit) params.limit = opts.limit;
  const res = Object.keys(params).length
    ? await api.get("/serviceDesk/whatsapp/review", { params })
    : await api.get("/serviceDesk/whatsapp/review");
  const data = unwrap(res) || {};
  return {
    items: data.items || [],
    alerts: data.alerts || [],
    counts: data.counts || { inbox: 0, outbox: 0, alerts: 0 },
    truncated: Boolean(data.truncated),
    nextCursor: typeof data.nextCursor === "string" && data.nextCursor ? data.nextCursor : null,
    canSeeAlerts: Boolean(data.canSeeAlerts),
  };
}

/** Qué hacer con las respuestas que pudieron salir al reprocesar. */
export type WaAmbiguousChoice = "resend" | "skip";

export interface WaReviewActionResult {
  status?: string | null;
  decision?: string | null;
  reason?: string | null;
  lastError?: string | null;
  dropReason?: string | null;
  /** `false` cuando el backend respondió 200 pero NO pasó lo pedido (descartado, volvió a fallar…). */
  ok?: boolean | null;
  /** El mensaje del servidor: dice qué pasó de verdad (no siempre es éxito). */
  message?: string | null;
}

const enc = encodeURIComponent;
/** Carga útil + el mensaje del servidor (que `unwrap` solo descartaría). */
const actionResult = (res: any): WaReviewActionResult => ({
  ...(unwrap(res) || {}),
  message: res?.data?.data?.message ?? null,
});
const noteBody = (note?: string) => (note && note.trim() ? { note: note.trim() } : {});

/** Reprocesar un evento de la bandeja de entrada (auditado). */
export async function reprocessInboxReview(
  eventKey: string,
  opts: { ambiguous?: WaAmbiguousChoice; note?: string } = {},
): Promise<WaReviewActionResult> {
  const body = { ...(opts.ambiguous ? { ambiguous: opts.ambiguous } : {}), ...noteBody(opts.note) };
  const res = await api.post(`/serviceDesk/whatsapp/review/inbox/${enc(eventKey)}/reprocess`, body);
  return actionResult(res);
}

/** Marcar resuelto un evento de la bandeja de entrada: empieza su caducidad. */
export async function resolveInboxReview(eventKey: string, note?: string): Promise<WaReviewActionResult> {
  const res = await api.post(`/serviceDesk/whatsapp/review/inbox/${enc(eventKey)}/resolve`, noteBody(note));
  return actionResult(res);
}

/** Reenvío DELIBERADO de un aviso del bot (nunca lo hace el sistema solo). */
export async function resendOutboxReview(entryId: string, note?: string): Promise<WaReviewActionResult> {
  const res = await api.post(`/serviceDesk/whatsapp/review/outbox/${enc(entryId)}/resend`, noteBody(note));
  return actionResult(res);
}

/** Reprocesar un archivo del cliente que no se guardó (idempotente). */
export async function reprocessOutboxReview(entryId: string, note?: string): Promise<WaReviewActionResult> {
  const res = await api.post(`/serviceDesk/whatsapp/review/outbox/${enc(entryId)}/reprocess`, noteBody(note));
  return actionResult(res);
}

/** Marcar resuelto un envío fallido o sin confirmar: empieza su caducidad. */
export async function resolveOutboxReview(entryId: string, note?: string): Promise<WaReviewActionResult> {
  const res = await api.post(`/serviceDesk/whatsapp/review/outbox/${enc(entryId)}/resolve`, noteBody(note));
  return actionResult(res);
}

/** «Enterado» de una alerta de tráfico descartado (se reabre si vuelve a pasar). */
export async function acknowledgeDropAlert(alertId: string, note?: string): Promise<WaReviewActionResult> {
  const res = await api.post(`/serviceDesk/whatsapp/review/alerts/${enc(alertId)}/acknowledge`, noteBody(note));
  return actionResult(res);
}

/** Por qué está en revisión, en palabras del staff. */
export function reviewKindLabel(item: WaReviewItem): string {
  if (item.source === "inbox") {
    if (item.kind === "raw") return "Mensaje de WhatsApp ilegible";
    if (item.ambiguousReplies > 0) return "Respuesta del bot en duda";
    return "Evento de WhatsApp sin procesar";
  }
  if (item.type === "inbound_media") return "Archivo del cliente que no se guardó";
  if (item.type === "delivery_status") return "Estado de entrega de WhatsApp que no se pudo aplicar";
  if (item.unconfirmed) return "Aviso del bot sin confirmar";
  if (item.errorKind === "dropped") return "Aviso del bot que no se mandó";
  if (item.errorKind === "ambiguous") return "Aviso del bot en duda";
  if (item.errorKind === "delivery_failed") return "Aviso del bot fallido según WhatsApp";
  return "Aviso del bot que no llegó";
}

// --- Presentación (pura, probada aparte) ------------------------------------

export const CATEGORY_META: Record<WaCaseCategory, { label: string; tone: "ok" | "warn" | "info" | "muted" }> = {
  bug: { label: "Falla (bug)", tone: "warn" },
  how_to: { label: "Duda de uso", tone: "info" },
  change_request: { label: "Solicitud de cambio", tone: "info" },
  follow_up: { label: "Seguimiento", tone: "muted" },
  chit_chat: { label: "Plática", tone: "muted" },
};

export const CASE_CATEGORIES = Object.keys(CATEGORY_META) as WaCaseCategory[];

/** Qué hizo el bot con cada mensaje (resultado de la simulación). */
export const ACTION_META: Record<string, string> = {
  ignored_chit_chat: "Plática: sin respuesta",
  kb_answered: "Respondida con la guía pública",
  awaiting_person: "Falta saber quién es",
  ticket_created: "Ticket creado",
  ticket_appended: "Agregado a un ticket existente",
  case_appended: "Sumado a la misma ráfaga",
  status_reply: "Respondió el estado",
  status_none: "Sin tickets que reportar",
  status_not_found: "Ticket no encontrado en el grupo",
  reference_not_found: "Ticket no encontrado",
  blocked_no_brand: "Sin ticket: el grupo no tiene marca",
  rate_limited: "Sin ticket: tope por hora alcanzado",
  ticket_failed: "Falló la creación del ticket",
  ticket_in_progress: "Ticket en creación",
};

export const CASE_STATUS_LABELS: Record<string, string> = {
  kb_answered: "Respondida con la guía",
  awaiting_person: "Falta identificar a quien escribe",
  ticketed: "Con ticket",
  blocked_no_brand: "Sin ticket (grupo sin marca)",
  rate_limited: "Sin ticket (tope por hora)",
  ticket_failed: "Falló la creación",
  open: "Abierto",
};

/** «85 %» — la confianza tal cual la guardó el backend (0 a 1). */
export function formatConfidence(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(Math.min(1, Math.max(0, value)) * 100)} %`;
}

export const DECISION_META: Record<string, { label: string; tone: "ok" | "warn" | "info" | "muted" }> = {
  channel_disabled: { label: "Canal desactivado", tone: "warn" },
  blocked_unlinked: { label: "Bloqueado: grupo sin compañía", tone: "warn" },
  staff_message: { label: "Mensaje de personal interno", tone: "muted" },
  ask_identity: { label: "Se preguntó quién es", tone: "info" },
  needs_confirmation: { label: "Falta confirmar al participante", tone: "info" },
  eligible: { label: "Puede generar ticket", tone: "ok" },
  participant_joined: { label: "Participante agregado", tone: "muted" },
  participant_left: { label: "Participante salió", tone: "muted" },
  group_updated: { label: "Grupo actualizado", tone: "muted" },
};

export const LINK_STATE_META: Record<WaLinkState, { label: string; tone: "ok" | "warn" | "info" }> = {
  confirmed: { label: "Confirmado", tone: "ok" },
  auto: { label: "Vinculado automáticamente", tone: "info" },
  suggested: { label: "Sugerido", tone: "info" },
  unknown: { label: "Sin identificar", tone: "warn" },
};

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  group_discovered: "Grupo detectado",
  group_created: "Grupo creado por la mesa",
  company_linked: "Compañía vinculada",
  company_unlinked: "Compañía desvinculada",
  brand_changed: "Marca del grupo cambiada",
  participant_linked: "Participante vinculado",
  participant_unlinked: "Participante desvinculado",
  participant_marked_staff: "Marcado como personal interno",
  participant_declared_identity: "El participante dijo quién es",
  participant_identity_corrected: "Nombre declarado corregido",
  participant_joined: "Participante agregado",
  participant_left: "Participante salió",
  unlinked_warning: "Aviso: grupo sin compañía",
  case_reclassified: "Clasificación corregida",
  bot_enabled: "Bot encendido",
  bot_disabled: "Bot apagado",
  bot_privacy_notice_sent: "Aviso de privacidad publicado",
  bot_privacy_notice_confirmed: "Aviso de privacidad confirmado por el staff",
  bot_limits_changed: "Topes del bot cambiados",
  bot_capped: "El bot llegó a su tope y se calló",
  bot_handoff_taken: "Una persona tomó el grupo",
  bot_handoff_released: "Grupo devuelto al bot",
  bot_human_requested: "El cliente pidió una persona / apagar el bot",
  group_link_conflict: "Conflicto de vínculo de compañía",
  // #713: un evento del webhook que agotó sus intentos (o cuya respuesta del
  // bot quedó en duda) espera a una persona; el detalle trae el motivo.
  wa_event_needs_review: "Evento de WhatsApp sin procesar: revisar",
  // #714: bandeja de salida. Lo que pudo haber salido nunca se reenvía solo;
  // el ticket tiene la nota interna con el texto y el id del envío.
  wa_outbound_failed: "Aviso del bot que no llegó (ver nota del ticket)",
  wa_outbound_unconfirmed: "Aviso del bot sin confirmar por WhatsApp",
  wa_outbound_resent: "Aviso del bot reenviado",
  wa_inbound_media_failed: "Archivo del cliente que no se guardó",
  // #728: acciones del staff desde la pantalla de revisión (todas auditadas).
  wa_review_inbox_reprocessed: "Evento reprocesado desde Revisión",
  wa_review_inbox_resent: "Respuesta en duda reenviada desde Revisión",
  wa_review_inbox_resolved: "Evento marcado resuelto en Revisión",
  wa_review_media_reprocessed: "Archivo del cliente reprocesado",
  wa_review_outbox_resolved: "Aviso del bot marcado resuelto en Revisión",
  // #730: un estado de entrega de WhatsApp que siempre fallaba al aplicarse.
  wa_outbound_status_needs_review: "Estado de entrega de WhatsApp sin aplicar: revisar",
  wa_review_status_reprocessed: "Estado de entrega reaplicado desde Revisión",
};

/** Etiqueta legible de una entrada de auditoría (con respaldo por si el backend añade acciones). */
export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] || action;
}

/**
 * ¿Puede este grupo generar solicitudes de cliente? Es la MISMA regla del
 * backend, repetida aquí sólo para pintar el estado: sin compañía, no.
 */
export function canCreateTickets(group: Pick<WaGroup, "companyId">): boolean {
  return Boolean(group.companyId);
}

/** El mejor candidato para un participante, o null si no hay ninguno claro. */
export function bestSuggestion(participant: Pick<WaParticipant, "suggestions">): WaSuggestion | null {
  const list = participant.suggestions || [];
  return list.find((s) => s.confidence === "high") || list[0] || null;
}
